import React from "react";
import "./ChatWidget.css";
import { 
  X, MessageCircle, User, SendHorizontal, RefreshCw, 
  Maximize2, Minimize2, MessageCircleQuestion, Calendar, 
  ThumbsUp, ThumbsDown, Clipboard, CheckCircle, ExternalLink 
} from "lucide-react";
import useChatWidget from "../../hooks/useChatWidget";
import Loader from "../common/Loader/Loader";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import AIStars from "../../assets/ai-stars.png";
import chatIcon from "../../assets/chat-icon.png";
import Logo from "../../assets/logo.png";
import LoaderRadial from "../common/Loader/LoaderRadial";
import FeedbackModal from "./FeedbackModal";

const customSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    "table", "thead", "tbody", "tfoot", "tr", "th", "td", "colgroup", "col", 
    "sup"
  ],
  attributes: {
    ...defaultSchema.attributes,
    // Allow additional attributes for links and other elements
    a: [...(defaultSchema.attributes?.a || []), "title", "className"],
    
    // Existing table attributes
    table: [...(defaultSchema.attributes?.table || []), "align", "border", "cellpadding", "cellspacing", "summary", "width"],
    th: [...(defaultSchema.attributes?.th || []), "colspan", "rowspan", "scope", "width"],
    td: [...(defaultSchema.attributes?.td || []), "colspan", "rowspan", "width"],
  },
};

const removeCodeBlocks = (content) => {
  return content
    .replace(/```[\s\S]*?```/g, (match) => match.replace(/```/g, ""))
    .replace(/\\n/g, "");
};

const formatChatContent = (content) => {
  
  const regex = /<sup>\[\[(.*?)\]\((.*?)\)\]<\/sup>/g;
  const matches = content.match(regex);
  let processedContent = content;
  if (matches) {
    matches.forEach(match => {
      // Extract text and URL from the match
      const innerRegex = /\[\[(.*?)\]\((.*?)\)\]/;
      const innerMatch = match.match(innerRegex);
      
      if (innerMatch && innerMatch.length >= 3) {
        const text = innerMatch[1];
        const url = innerMatch[2];
        
        // Replace with markdown where the icon will be
        processedContent = processedContent.replace(
          match,
          ` [CITATION_LINK:${text}](${url})`
        );
      }
    });
  }
  
  return processedContent;
}

const ChatWidget = ({version}) => {
  const {
    isOpen,
    isLoading,
    firstChat,
    messages,
    userInput,
    chatBodyRef,
    isExpanded,
    inputRef,
    setUserInput,
    handleToggle,
    handleSendMessage,
    handleOptionSelection,
    handleClose,
    handleReset,
    handleExpand,
    handleFeedback,
    handleCopy,
    activeSuggestedQuestions,
    copiedMessageIndex,
    handleCommentFeedback,
    commentModalState,
    openCommentModal,
    closeCommentModal,
    isSessionExpired,
    resetInactivityTimer
  } = useChatWidget();
 
  return (
    <>
      {isExpanded && (
        <div className="chat-modal-backdrop active" onClick={handleExpand} />
      )}
      <button
        className={`chat-trigger-btn ${
          isOpen || isExpanded ? "hide-component" : ""
        }`}
        onClick={handleToggle}
        aria-label="Open chat assistant"
      >
        <img src={chatIcon} alt="AI Chat" />
      </button>
      <div
        id="chatWidget"
        className={`chat-widget--container ${isOpen ? "showChat" : ""} ${
          isExpanded ? "expanded" : ""
        }`}
        onClick={resetInactivityTimer}
        onScroll={resetInactivityTimer}
      >
        <div className="chat-widget-header">
        <div className="logo--wrapper">
          <div className="logo">
            <img src={Logo} alt="" />
          </div>
          <h3>Doug <span className="doug-badge">Property Tax</span></h3>
        </div>
          <div style={{ display: "flex", gap: "10px" }}>
          <div className="header-button-wrapper">
          </div>
            <div className="header-button-wrapper">
              <button className="close-btn" onClick={handleExpand} aria-label={isExpanded ? "Minimize chat" : "Maximize chat"}>
                {isExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                <span className="tooltip">
                  {isExpanded ? "Minimize" : "Maximize"}
                </span>
              </button>
            </div>

            <div className="header-button-wrapper">
              <button className="close-btn" onClick={handleReset} aria-label="Reset conversation">
                <RefreshCw size={20} />
                <span className="tooltip">Reset conversation</span>
              </button>
            </div>

            <div className="header-button-wrapper">
              <button className="close-btn" onClick={handleClose} aria-label="Close chat">
                <X size={20} />
                <span className="tooltip">Close chat</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Session expired overlay - moved here, outside scrollable area */}
        {isSessionExpired && (
          <div className="session-expired-overlay">
            <div className="session-expired-content">
              <h3>Session Expired</h3>
              <p>Your session has ended due to inactivity.</p>
              <button 
                className="chat-inline-button"
                onClick={handleReset}
              >
                Start New Chat
              </button>
            </div>
          </div>
        )}
        
        <div
          ref={chatBodyRef}
          className={`chat-widget-body ${firstChat ? "first-chat" : ""}`}
        >
          {messages.map((elm, i) => (
            <div
              key={`msg${i}`}
              className={`chat-response--wrapper ${
                elm.role === "assistant" ? "" : "reverse"
              }`}
            >
              <div className="logo">
                {elm.role === "assistant" ? (
                  <img src={Logo} alt="" />
                ) : (
                  <User fill="#1F263E" />
                )}
              </div>
              <div className="chat--wrapper">
                <div className="chat-response">
                  {isLoading &&
                  elm.role === "assistant" &&
                  elm.content === "" &&
                  i === messages.length - 1 ? (
                    <Loader />
                  ) : (
                    <>
                      {elm.role === "assistant" ? (
                        <>
                        <ReactMarkdown
                          className="chat-message-markdown"
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[
                            rehypeRaw,
                            [rehypeSanitize, customSchema],
                          ]}
                          components={{
                            table: ({ node, ...props }) => (
                              <div className="table-container">
                                <table {...props} />
                              </div>
                            ),
                            // Handle links - especially our citation links
                            a: ({ node, children, href, ...props }) => {
                              // Check if this is one of our citation links
                              const childrenStr = children?.toString() || "";
                              if (childrenStr.startsWith("CITATION_LINK:")) {
                                const title = childrenStr.replace("CITATION_LINK:", "");
                                return (
                                  <a 
                                    href={href} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="external-link-icon" 
                                    title={title}
                                  >
                                    <ExternalLink size={16} />
                                  </a>
                                );
                              }
                              
                              // Filter out back references and footnote links
                              if (href?.startsWith('#fnref') || 
                                  (children && (children[0] === '↩' || (children[0] && children[0].includes && children[0].includes('↩'))))) {
                                return null;
                              }
                              
                              // Regular links remain unchanged
                              return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
                            },
                            // Remove any remaining sup elements that might contain citations
                            sup: ({ node, children }) => {
                              // If it looks like a citation, don't render it
                              if (children && 
                                  React.Children.toArray(children).some(child => 
                                    (typeof child === 'string' && child.includes('[') && child.includes(']')) ||
                                    (React.isValidElement(child) && child.type === 'a')
                                  )) {
                                return null;
                              }
                              
                              // Otherwise render as normal sup
                              return <sup>{children}</sup>;
                            }
                          }}
                          skipHtml={false}
                        >
                          {formatChatContent(elm.content)}
                        </ReactMarkdown>
                          {isLoading && i === messages.length - 1 && (
                            <LoaderRadial />
                          )}
                          {!isLoading && (
                            <div className="message-feedback">
                            <div className="feedback-wrapper">
                              <button 
                                className={`feedback-btn ${elm.feedback_type === "up" ? "active" : ""}`} 
                                onClick={() => handleFeedback(i, elm.feedback_type === "up" ? null : "up")}
                                aria-label="Good Response"
                                aria-pressed={elm.feedback_type === "up"}
                              >
                                <ThumbsUp size={16} fill={elm.feedback_type === "up" ? "#1F263E" : "none"} stroke="#1F263E" />
                              </button>
                              <span className="tooltip">Good Response</span>
                            </div>
                            
                            <div className="feedback-wrapper">
                              <button 
                                className={`feedback-btn ${elm.feedback_type === "down" ? "active" : ""}`} 
                                onClick={() => handleFeedback(i, elm.feedback_type === "down" ? null : "down")}
                                aria-label="Bad Response"
                                aria-pressed={elm.feedback_type === "down"}
                              >
                                <ThumbsDown size={16} fill={elm.feedback_type === "down" ? "#1F263E" : "none"} stroke="#1F263E" />
                              </button>
                              <span className="tooltip">Bad Response</span>
                            </div>
                            
                            {/* Comment button - only shows if feedback is given */}
                            {elm.feedback_type && (
                              <div className="feedback-wrapper">
                                <button 
                                  className="feedback-btn" 
                                  onClick={() => openCommentModal(i)}
                                  aria-label={elm.feedback_comment ? "Edit Comment" : "Add Comment"}
                                >
                                  <MessageCircle 
                                    size={16} 
                                    fill={elm.feedback_comment ? "#1F263E" : "none"} 
                                    stroke="#1F263E" 
                                  />
                                </button>
                                <span className="tooltip">
                                  {elm.feedback_comment ? "Edit Comment" : "Add Comment"}
                                </span>
                              </div>
                            )}
                            
                            <div className="feedback-wrapper">
                              <button 
                                className="feedback-btn" 
                                onClick={() => handleCopy(elm.content, i)}
                                aria-label={copiedMessageIndex === i ? "Copied to clipboard" : "Copy message"}
                              >
                                {copiedMessageIndex === i ? (
                                  <CheckCircle size={16} stroke="#1F263E" />
                                ) : (
                                  <Clipboard size={16} stroke="#1F263E" />
                                )}
                              </button>
                              <span className="tooltip">
                                {copiedMessageIndex === i ? "Copied!" : "Copy"}
                              </span>
                            </div>
                          </div>
                          )}
                        </>
                      ) : (
                        <ReactMarkdown
                          className="chat-message-markdown"
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[
                            rehypeRaw,
                            [rehypeSanitize, customSchema],
                          ]}
                          components={{
                            table: ({ node, ...props }) => (
                              <div className="table-container">
                                <table {...props} />
                              </div>
                            ),
                            a: ({ node, children, href, ...props }) => (
                              <button
                                onClick={() =>
                                  window.open(
                                    href,
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                                }
                                className="question-button"
                              >
                                {children}
                              </button>
                            ),
                          }}
                          skipHtml={false}
                        >
                          {removeCodeBlocks(elm.content)}
                        </ReactMarkdown>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {firstChat && (
            <div className="chat-options--container">
              <button
                className="question-button"
                onClick={() => {
                  handleOptionSelection("How can I protest my property assessment?");
                }}
              >
                How can I protest my property assessment?
              </button>
              <button
                className="question-button"
                onClick={() => {
                  handleOptionSelection(
                    "How are property taxes calculated in Douglas County?"
                  );
                }}
              >
                How are property taxes calculated in Douglas County?
              </button>
              <button
                className="question-button"
                onClick={() => {
                  handleOptionSelection(
                    "Tell me about the Board of Equalization."
                  );
                }}
              >
                Tell me about the Board of Equalization.
              </button>
            </div>
          )}
        </div>
        <div className="chat-widget-bottom">
          <div className="chat-widget-footer">
            <form
              className="chat-widget-input--wrapper"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
            >
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask a question..."
                value={userInput}
                onChange={(e) => {
                  resetInactivityTimer();
                  setUserInput(e.target.value);
                }}
                disabled={isLoading || isSessionExpired}
                onFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  document.body.style.zoom = 1;
                }}
              />
              <button 
                type="submit" 
                className="send-btn" 
                disabled={isLoading || isSessionExpired}
                aria-label="Send message"
              >
                <SendHorizontal fill="#1F263E" stroke="#fff" strokeWidth={1} />
              </button>
            </form>
          </div>

          {activeSuggestedQuestions && !isSessionExpired && (
            <div className="suggested-questions">
              {activeSuggestedQuestions.map((button, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionSelection(button.text)}
                  className="suggested-button"
                >
                  {button.text}
                </button>
              ))}
            </div>
          )}

          <div className="chat-disclaimer">
            <div>Doug is experimental. The more you use it the better it gets.</div>
            {version && <div className="version-text">v{version}</div>}
          </div>
        </div>
      </div>
      <FeedbackModal 
        isOpen={commentModalState.isOpen}
        initialComment={commentModalState.initialComment}
        onClose={closeCommentModal}
        onSubmit={(comment) => {
          handleCommentFeedback(commentModalState.messageIndex, comment);
        }}
      />
    </>
  );
};

export default ChatWidget;